import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { maskValue, unmaskValue } from '../util/mask';
import { addEventListener } from '@niaratech/niara-react-components';
export type Params = {
  mask?: string | null | undefined;
  allowLongerThanMask?: boolean | string | null | undefined;
  ignoreChangesOnValue?: boolean;
  inputValue: string | null | undefined;
  setInputValue: React.Dispatch<React.SetStateAction<string>> | React.Dispatch<React.SetStateAction<string | null>> | React.Dispatch<React.SetStateAction<string | undefined>>;
};
type Return = {
  inputRef: React.MutableRefObject<HTMLInputElement>;
  unmaskedValue: string;
  inputValue: string | null | undefined;
  setInputValue: React.Dispatch<React.SetStateAction<string>> | React.Dispatch<React.SetStateAction<string | null>> | React.Dispatch<React.SetStateAction<string | undefined>>;
};
/**
 * Utilidade para ter input text com máscara
 */

const useMaskedInputText = ({
  mask,
  allowLongerThanMask,
  inputValue,
  setInputValue
}: Params): Return => {
  const textInputRef = useRef<HTMLInputElement>();
  const prevMaskRef = useRef<string>(mask);
  const currentInputValue = setInputValue ? inputValue : textInputRef.current ? textInputRef.current?.value : inputValue;
  const prevInputValueRef = useRef<string>(currentInputValue);
  const maskedValue = mask && currentInputValue ? maskValue(currentInputValue, mask, allowLongerThanMask) : currentInputValue;
  useLayoutEffect(() => {
    // ao fim da renderização, se o valor de inputValue não for igual ao valor mascarado, atualiza - e grava a posição do cursor
    if (mask && currentInputValue) {
      if (currentInputValue !== maskedValue) {
        // precisa atualizar o valor do input value e corrigir a posição do cursor

        // No próximo render, disparado pela chamada ao setInputValue, o react vai atualizar o
        // value do inputRef - e isso zera a seleção do input para o fim dele.

        // Por esse motivo, vou setar agora o valor direto no input e restaurar a seleção.

        // Vou manipular neste effect o value do <input/> e depois setar o valor em setInputValue.
        if (textInputRef.current) {
          const selection: [number, number] = [textInputRef.current.selectionStart, textInputRef.current.selectionEnd];
          const cursorAtEnd = selection[0] === currentInputValue.length;
          if (cursorAtEnd) {
            // colocar a seleção ao fim do input
            textInputRef.current.value = maskedValue;
            textInputRef.current.setSelectionRange(maskedValue.length, maskedValue.length);
          } else {
            // vou mascarar o inputValue até o início da seleção e colocar o cursor no fim do valor mascarado

            const maskedValueUntilSelection = maskValue(currentInputValue.substring(0, selection[0]), mask, allowLongerThanMask);
            const diff = maskedValueUntilSelection.length - selection[0];
            if (maskValue(currentInputValue, mask, allowLongerThanMask) === maskValue(prevInputValueRef.current, mask, allowLongerThanMask) && currentInputValue.length < prevInputValueRef.current?.length) {
              // Apagou caractere da máscara - não mexe e deixa para mascarar no onblur.
              return () => {};
            }
            textInputRef.current.value = maskedValue;
            // se maskedValueUntilSelection.length < selection[0]:
            //  1) - colou " 2 1 1 2   34 "
            //     - currentInputValue: "2 1 1 2   34 1234"
            //     - masked: "(21) 1234-1234"
            //     - até cursor: "2 1 1 2   34 "
            //     - masked até o cursor: : "(21) 1234"
            //     - diff: -4
            //     - masked após o cursor: : "(21) 1234-" (SIM)
            //  2) - "(*11) 12345"
            //     - {Delete}
            //     - current input: "(1) 12345"
            //     - masked: : "(11) 2345"
            //     - até cursor: "("
            //     - masked até o cursor: ""
            //     - masked após o cursor: : "(" (SIM)
            //  2b) - "(1*1) 12345"
            //     - {Delete}
            //     - current input: "(1) 12345"
            //     - masked: : "(11) 2345"
            //     - até cursor: "(1"
            //     - masked até o cursor: "(1"
            //     - masked pós o cursor: "(11" (NÃO)
            //  3) - "(1*1"
            //     - {Delete}
            //     - current input: "(1"
            //     - masked: : "1" (mudou a máscara)
            //     - até cursor: "("
            //     - masked até o cursor: ""
            //     - masked pós o cursor: "1" (NÃO)
            //  4) - "(32) 5888*4010"
            //     - {Delete}
            //     - current input: "(32) 5888010"
            //     - masked: : "(32) 5888-010"
            //     - até cursor: "(32) 5888"
            //     - masked até o cursor: "(32) 5888"
            //     - masked pós o cursor: "(32) 5888-" (SIM)

            let x = selection[0] + diff;
            // se após o cursor for um caractere de máscara, reposicionar o cursor
            while (x < maskedValue.length && /\W/.test(maskedValue[x])) {
              x++;
            }
            textInputRef.current.setSelectionRange(x, x);
          }
        }
        if (setInputValue) {
          setInputValue(maskedValue);
        }
      }
    }
  }, [mask, setInputValue, allowLongerThanMask, currentInputValue, maskedValue]);
  useEffect(() => {
    if (textInputRef.current) {
      let ignore = false;
      const input = textInputRef.current;
      // listener para atualizar a máscara no onblur
      const onBlur: EventListener = event => {
        if (!ignore) {
          if (input) {
            input.value = maskedValue;
          }
          setInputValue(maskedValue);
        }
      };
      input.addEventListener('blur', onBlur);
      return () => {
        ignore = true;
        input.removeEventListener('blur', onBlur);
      };
    }
  }, [maskedValue, setInputValue]);
  useEffect(() => {
    prevMaskRef.current = mask;
    prevInputValueRef.current = currentInputValue;
  });
  return {
    inputRef: textInputRef,
    unmaskedValue: mask ? unmaskValue(inputValue, mask, allowLongerThanMask) : inputValue,
    inputValue: maskedValue,
    setInputValue
  };
};
export default useMaskedInputText;